<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import ProjectMilestonesAPI from 'dashboard/api/projectMilestones';
import Button from 'dashboard/components-next/button/Button.vue';
import ProjectEmptyState from './ProjectEmptyState.vue';

const props = defineProps({
  projectId: {
    type: [String, Number],
    required: true,
  },
  phases: {
    type: Array,
    default: () => [],
  },
});

const { t } = useI18n();

const STATUSES = ['pending', 'achieved', 'missed', 'at_risk'];

const STATUS_CLASS = {
  pending: 'bg-n-alpha-1 text-n-slate-11',
  achieved: 'bg-n-teal-3 text-n-teal-11',
  missed: 'bg-n-ruby-3 text-n-ruby-11',
  at_risk: 'bg-n-amber-3 text-n-amber-11',
};

const milestones = ref([]);
const isLoading = ref(false);
const isSaving = ref(false);
const showForm = ref(false);

const blankForm = () => ({
  name: '',
  description: '',
  target_date: '',
  status: 'pending',
  is_gate: false,
  project_phase_id: '',
});

const form = ref(blankForm());

const orderedMilestones = computed(() =>
  [...milestones.value].sort((a, b) => (a.position || 0) - (b.position || 0))
);

const statusClass = status => STATUS_CLASS[status] || STATUS_CLASS.pending;
const statusLabel = status =>
  t(`PROJECTS.MILESTONES.STATUS.${status.toUpperCase()}`);

const loadMilestones = async () => {
  isLoading.value = true;
  try {
    const { data } = await ProjectMilestonesAPI.get(props.projectId);
    milestones.value = data || [];
  } catch (error) {
    milestones.value = [];
    useAlert(t('PROJECTS.MILESTONES.LOAD_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const resetForm = () => {
  form.value = blankForm();
  showForm.value = false;
};

const createMilestone = async () => {
  const name = form.value.name.trim();
  if (!name || isSaving.value) return;
  isSaving.value = true;
  try {
    const payload = {
      name,
      description: form.value.description.trim() || null,
      target_date: form.value.target_date || null,
      status: form.value.status,
      is_gate: form.value.is_gate,
    };
    if (form.value.project_phase_id) {
      payload.project_phase_id = Number(form.value.project_phase_id);
    }
    await ProjectMilestonesAPI.create(props.projectId, payload);
    resetForm();
    await loadMilestones();
  } catch (error) {
    useAlert(t('PROJECTS.MILESTONES.SAVE_ERROR'));
  } finally {
    isSaving.value = false;
  }
};

const changeStatus = async (milestone, status) => {
  if (milestone.status === status) return;
  try {
    await ProjectMilestonesAPI.update(props.projectId, milestone.id, {
      status,
    });
    await loadMilestones();
  } catch (error) {
    useAlert(t('PROJECTS.MILESTONES.SAVE_ERROR'));
  }
};

const deleteMilestone = async milestone => {
  // eslint-disable-next-line no-alert
  if (!window.confirm(t('PROJECTS.MILESTONES.DELETE_CONFIRM'))) return;
  try {
    await ProjectMilestonesAPI.delete(props.projectId, milestone.id);
    milestones.value = milestones.value.filter(m => m.id !== milestone.id);
  } catch (error) {
    useAlert(t('PROJECTS.MILESTONES.DELETE_ERROR'));
  }
};

const hasMilestones = computed(() => milestones.value.length > 0);

watch(() => props.projectId, loadMilestones);
onMounted(loadMilestones);
</script>

<template>
  <div class="flex flex-col gap-4">
    <!-- Header -->
    <div class="flex items-center justify-between gap-2">
      <h3 class="text-sm font-medium text-n-slate-12">
        {{ t('PROJECTS.MILESTONES.TITLE') }}
      </h3>
      <Button
        variant="faded"
        color="blue"
        size="sm"
        :icon="showForm ? 'i-lucide-x' : 'i-lucide-plus'"
        :label="
          showForm
            ? t('PROJECTS.MILESTONES.CANCEL')
            : t('PROJECTS.MILESTONES.ADD')
        "
        @click="showForm ? resetForm() : (showForm = true)"
      />
    </div>

    <!-- Create form -->
    <div
      v-if="showForm"
      class="flex flex-col gap-3 p-4 border rounded-xl border-n-weak bg-n-solid-2"
    >
      <input
        v-model="form.name"
        type="text"
        :placeholder="t('PROJECTS.MILESTONES.NAME_PLACEHOLDER')"
        class="w-full h-8 px-2.5 text-sm border rounded-md reset-base bg-n-alpha-black2 border-n-weak text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:outline-1 focus:outline-n-brand"
      />
      <textarea
        v-model="form.description"
        rows="2"
        :placeholder="t('PROJECTS.MILESTONES.DESCRIPTION_PLACEHOLDER')"
        class="w-full px-2.5 py-1.5 text-sm border rounded-md reset-base resize-none bg-n-alpha-black2 border-n-weak text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:outline-1 focus:outline-n-brand"
      />
      <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label class="flex flex-col gap-1 text-xs text-n-slate-10">
          {{ t('PROJECTS.MILESTONES.FIELDS.TARGET_DATE') }}
          <input
            v-model="form.target_date"
            type="date"
            class="w-full h-8 px-2 text-sm border rounded-md reset-base bg-n-alpha-black2 border-n-weak text-n-slate-12 focus:outline-none focus:outline-1 focus:outline-n-brand"
          />
        </label>
        <label class="flex flex-col gap-1 text-xs text-n-slate-10">
          {{ t('PROJECTS.MILESTONES.FIELDS.STATUS') }}
          <select
            v-model="form.status"
            class="w-full h-8 px-2 text-sm border rounded-md reset-base bg-n-alpha-black2 border-n-weak text-n-slate-12 focus:outline-none focus:outline-1 focus:outline-n-brand"
          >
            <option v-for="s in STATUSES" :key="s" :value="s">
              {{ statusLabel(s) }}
            </option>
          </select>
        </label>
      </div>
      <label
        v-if="phases.length"
        class="flex flex-col gap-1 text-xs text-n-slate-10"
      >
        {{ t('PROJECTS.MILESTONES.FIELDS.PHASE') }}
        <select
          v-model="form.project_phase_id"
          class="w-full h-8 px-2 text-sm border rounded-md reset-base bg-n-alpha-black2 border-n-weak text-n-slate-12 focus:outline-none focus:outline-1 focus:outline-n-brand"
        >
          <option value="">
            {{ t('PROJECTS.MILESTONES.NO_PHASE') }}
          </option>
          <option v-for="phase in phases" :key="phase.id" :value="phase.id">
            {{ phase.name }}
          </option>
        </select>
      </label>
      <label
        class="flex items-center gap-2 text-sm cursor-pointer text-n-slate-12"
      >
        <input
          v-model="form.is_gate"
          type="checkbox"
          class="rounded reset-base accent-n-brand"
        />
        {{ t('PROJECTS.MILESTONES.IS_GATE') }}
      </label>
      <div class="flex items-center justify-end gap-2">
        <Button
          variant="ghost"
          color="slate"
          size="sm"
          :label="t('PROJECTS.MILESTONES.CANCEL')"
          @click="resetForm"
        />
        <Button
          size="sm"
          color="blue"
          icon="i-lucide-check"
          :label="t('PROJECTS.MILESTONES.SAVE')"
          :is-loading="isSaving"
          :disabled="!form.name.trim()"
          @click="createMilestone"
        />
      </div>
    </div>

    <!-- Empty state -->
    <ProjectEmptyState
      v-if="!hasMilestones && !isLoading"
      icon="i-lucide-flag"
      :title="t('PROJECTS.MILESTONES.EMPTY')"
    >
      <template v-if="!showForm" #action>
        <Button
          size="sm"
          color="blue"
          icon="i-lucide-plus"
          :label="t('PROJECTS.MILESTONES.ADD')"
          @click="showForm = true"
        />
      </template>
    </ProjectEmptyState>

    <!-- List -->
    <ul v-else class="flex flex-col divide-y divide-n-weak/60">
      <li
        v-for="milestone in orderedMilestones"
        :key="milestone.id"
        class="flex items-center gap-3 py-2.5 group"
      >
        <span
          class="flex items-center justify-center rounded-full size-8 shrink-0 bg-n-alpha-1 text-n-slate-10"
        >
          <span class="i-lucide-flag size-4" />
        </span>
        <div class="flex flex-col min-w-0 gap-1">
          <div class="flex items-center gap-2">
            <span class="text-sm font-medium truncate text-n-slate-12">
              {{ milestone.name }}
            </span>
            <span
              v-if="milestone.is_gate"
              class="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-md shrink-0 bg-n-blue-3 text-n-blue-11"
            >
              <span class="i-lucide-shield-check size-3" />
              {{ t('PROJECTS.MILESTONES.GATE') }}
            </span>
          </div>
          <div
            class="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-n-slate-10"
          >
            <span v-if="milestone.target_date" class="flex items-center gap-1">
              <span class="i-lucide-calendar size-3.5" />
              {{ t('PROJECTS.MILESTONES.TARGET') }}: {{ milestone.target_date }}
            </span>
            <span v-if="milestone.actual_date" class="flex items-center gap-1">
              <span class="i-lucide-check-circle size-3.5" />
              {{ t('PROJECTS.MILESTONES.ACTUAL') }}: {{ milestone.actual_date }}
            </span>
          </div>
        </div>
        <div class="flex items-center gap-1.5 ml-auto shrink-0">
          <span
            class="inline-flex items-center px-2 py-0.5 text-xs rounded-md"
            :class="statusClass(milestone.status)"
          >
            {{ statusLabel(milestone.status) }}
          </span>
          <select
            :value="milestone.status"
            class="h-8 px-2 text-xs border rounded-md reset-base bg-n-alpha-black2 border-n-weak text-n-slate-12 focus:outline-none focus:outline-1 focus:outline-n-brand"
            @change="changeStatus(milestone, $event.target.value)"
          >
            <option v-for="s in STATUSES" :key="s" :value="s">
              {{ statusLabel(s) }}
            </option>
          </select>
          <Button
            v-tooltip.top="t('PROJECTS.MILESTONES.DELETE')"
            variant="ghost"
            color="ruby"
            size="xs"
            icon="i-lucide-trash-2"
            class="transition-opacity opacity-0 group-hover:opacity-100"
            @click="deleteMilestone(milestone)"
          />
        </div>
      </li>
    </ul>
  </div>
</template>
